import type ReportProperties from "./Report.properties.ts";
import { type ChangeEvent, useMemo, useRef, useState } from "react";
import type { MetaOutput } from "../../generated/fresh-guard-api";
import { toast } from "react-toastify";
import { toastOptions } from "../../tools/helpers/ToastHelper.ts";
import type { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import type { SelectType } from "../../components/shared/select/Select.type.ts";
import { useReportingCsvExport } from "../../apis/reporting/useReportingCsvExport.ts";
import { useTranslation } from "react-i18next";
import {
  extractFilenameFromHeader,
  triggerDownload,
} from "../../tools/helpers/FileHelper.ts";
import { AxiosHeaders } from "axios";
import {
  type ReportingOrderDTO,
  useReportingSelect,
} from "../../apis/reporting/useReportingSelect.ts";
import { useReportHelper } from "./Report.helper.ts";
import type {
  ReportingMarketComponentHistoryType,
  ReportingMarketComponentType,
  ReportingUserType,
} from "../../apis/reporting/Reporting.type.ts";

export const useReportController = <T>(props: ReportProperties) => {
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact<T>>(null);
  const [metaOutput, setMetaOutput] = useState<MetaOutput>();
  const [sortedBy, setSortedBy] = useState<
    { colId: string; sort: "asc" | "desc" }[]
  >([]);
  const columnDefs: ColDef[] = props.columnDef;
  const pageLimits: SelectType[] = [
    { value: "100", label: "100" },
    { value: "200", label: "200" },
    { value: "500", label: "500" },
    { value: "1000", label: "1000" },
  ];

  const {
    buildOrderDTO,
    marketComponentOrderMap,
    marketComponentHistoryOrderMap,
    userOrderMap,
  } = useReportHelper();

  const order: ReportingOrderDTO = useMemo(() => {
    if (sortedBy.length === 0) return null;

    switch (props.type) {
      case "MARKET_COMPONENT_HISTORY": {
        const dto = buildOrderDTO<ReportingMarketComponentHistoryType>(
          sortedBy,
          marketComponentHistoryOrderMap,
        );
        return dto as ReportingMarketComponentHistoryType;
      }
      case "MARKET_COMPONENT_30_60_90_VERIFICATION":
      case "MARKET_COMPONENT_WITHOUT_CRITICAL_BBD":
      case "MARKET_COMPONENT_WHERE_SHORT_DESCRIPTION_NOT_EQUALS_NUMBER": {
        const dto = buildOrderDTO<ReportingMarketComponentType>(
          sortedBy,
          marketComponentOrderMap,
        );
        return dto as ReportingMarketComponentType;
      }
      case "USER": {
        const dto = buildOrderDTO<ReportingUserType>(sortedBy, userOrderMap);
        return dto as ReportingUserType;
      }
      default:
        return null;
    }
  }, [
    sortedBy,
    props.type,
    buildOrderDTO,
    marketComponentHistoryOrderMap,
    marketComponentOrderMap,
    userOrderMap,
  ]);

  const { data, isLoading } = useReportingSelect(metaOutput, props.type, order);
  const { mutate, isPending } = useReportingCsvExport();

  const metaFromServer = data ? data[0].metaOutput : null;

  const firstClick = () => {
    if (metaFromServer?.page !== 1) {
      setMetaOutput({
        limit: metaFromServer?.limit,
        page: 1,
        totalItems: metaFromServer?.totalItems,
        totalPages: metaFromServer?.totalPages,
      });
    }
  };

  const nextClick = () => {
    const page = (metaFromServer?.page ?? 1) + 1;

    if (metaFromServer?.totalPages && page <= metaFromServer.totalPages) {
      setMetaOutput({
        limit: metaFromServer?.limit,
        page: page,
        totalItems: metaFromServer?.totalItems,
        totalPages: metaFromServer?.totalPages,
      });
    }
  };

  const prevClick = () => {
    const page = (metaFromServer?.page ?? 1) - 1;

    if (page > 0) {
      setMetaOutput({
        limit: metaFromServer?.limit,
        page: page,
        totalPages: metaFromServer?.totalPages,
      });
    }
  };

  const lastClick = () => {
    if (metaFromServer?.page !== metaFromServer?.totalPages) {
      setMetaOutput({
        limit: metaFromServer?.limit,
        page: metaFromServer?.totalPages,
        totalItems: metaFromServer?.totalItems,
        totalPages: metaFromServer?.totalPages,
      });
    }
  };

  const limitChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setMetaOutput({
      limit: parseInt(event.target.value),
      page: 1,
      totalItems: metaFromServer?.totalItems,
      totalPages: metaFromServer?.totalPages,
    });
  };

  const sortChanged = (
    sortModel: { colId: string; sort: "asc" | "desc" }[],
  ) => {
    setSortedBy(sortModel);

    if (!metaFromServer) return;

    // On sort change reset to first page while keeping current limit & totals
    setMetaOutput({
      limit: metaFromServer.limit,
      page: 1,
      totalItems: metaFromServer.totalItems,
      totalPages: metaFromServer.totalPages,
    });
  };

  const exportCSV = () => {
    mutate(
      { report: props.type },
      {
        onSuccess: (dataCsvExport) => {
          const filename = extractFilenameFromHeader(
            dataCsvExport.headers as AxiosHeaders,
          );
          const data = dataCsvExport.data.toString();
          const contentType = dataCsvExport.headers["content-type"];
          const mime =
            typeof contentType === "string"
              ? contentType
              : contentType != null
                ? String(contentType)
                : "text/csv";

          triggerDownload(filename, data, mime);

          toast.success(t("success.csvExportDownloaded"), toastOptions);
        },
        onError: (error) => {
          toast.error(error.message, toastOptions);
        },
      },
    );
  };

  return {
    gridRef,
    data,
    columnDefs,
    metaFromServer,
    pageLimits,
    firstClick,
    nextClick,
    prevClick,
    lastClick,
    limitChange,
    exportCSV,
    loading: isLoading || isPending,
    sortChanged,
  };
};
